import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import RevealText from "./RevealText";
import KorvTube from "./KorvTube";
import { HERO } from "../constants/testIds";

const TUBES = [
  { color: "#141212", cap: "dark", sub: "LIP BALM" },
  { color: "#e6ddcf", cap: "light", sub: "LIP GLOSS" },
  { color: "#b8a48c", cap: "same", sub: "LIP GLOSS" },
  { color: "#3a2530", cap: "same", sub: "LIP GLOSS" },
  { color: "#c9a37a", cap: "dark", sub: "LIP GLOSS" },
];

export default function HeroCinematic() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.35]);

  return (
    <section
      ref={ref}
      data-testid={HERO.section}
      className="relative w-full min-h-[100svh] overflow-hidden bg-bone spot-cursor"
    >
      {/* Ambient parallax composition — five tubes arranged as an editorial still-life */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 76% 46%, hsl(35 25% 82%) 0%, hsl(39 30% 89%) 55%)",
          }}
        />
        <div className="absolute right-[-3%] top-[10%] w-[64%] md:w-[52%] max-w-[900px] aspect-[16/10]">
          {TUBES.map((t, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${i * 15}%`,
                top: `${(i % 2 === 0 ? 0 : 8) + i * 1}%`,
                width: "22%",
                zIndex: 10 + i,
              }}
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 1.6,
                delay: 1.9 + i * 0.09,
                ease: [0.2, 0.7, 0.1, 1],
              }}
            >
              <KorvTube color={t.color} cap={t.cap} sublabel={t.sub} animate={false} />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Top hairline meta row */}
      <div className="absolute top-[84px] md:top-[104px] left-0 right-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between text-[10px] uppercase tracking-editorial text-ink/55">
          <span>House of Korv — Volume I</span>
          <span className="hidden md:inline">MMXXV / MMXXVI</span>
          <span>Chapter 01 · The Lip</span>
        </div>
      </div>

      {/* Editorial copy block */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 h-full min-h-[100svh] flex flex-col justify-end pb-16 md:pb-24 pt-40 md:pt-48">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.7 }}
          className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-8"
        >
          Volume I — Lip. Balm. Gloss. Oil.
        </motion.p>

        <RevealText
          as="h1"
          delay={1.7}
          className="font-serif-display text-[13vw] md:text-[10vw] lg:text-[8.8vw] leading-[0.9] tracking-[-0.03em] max-w-[16ch]"
          lines={[
            "A quiet",
            <span key="i" className="italic font-serif-italic">
              architecture
            </span>,
            "for the lip.",
          ]}
        />

        <div className="mt-14 grid grid-cols-12 gap-6 items-end">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 2.4 }}
            className="col-span-12 md:col-span-5 text-[15px] md:text-[16px] leading-[1.55] text-ink/75 max-w-[36ch]"
          >
            Five compositions. Slow-pressed oils, mineral pigment, considered
            restraint. The first volume of a house designed for the long form.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 2.55 }}
            className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end"
          >
            <Link
              to="/collection"
              data-testid={HERO.cta}
              className="editorial-link text-[11px] uppercase tracking-editorial"
            >
              View the Collection
              <span aria-hidden>—→</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Bottom meta */}
      <motion.div
        data-testid={HERO.scrollHint}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 2.8 }}
        className="absolute bottom-6 md:bottom-8 left-0 right-0 z-10"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between text-[10px] uppercase tracking-editorial text-ink/55">
          <span>N° 001 — 005</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            className="flex items-center gap-3"
          >
            Scroll
            <span className="w-6 h-[1px] bg-ink/60 inline-block" />
          </motion.span>
          <span>korv.</span>
        </div>
      </motion.div>
    </section>
  );
}
